/**
 * FeaturedTestTrees — pinned showcase strip that always renders the
 * two "test family trees" (Vellaringattu, Kurisummoottil) so admins
 * can jump straight into them from Heritage or the IndianFamilyTree
 * sibling site without hunting through the browse list.
 *
 * Fetches each tree by slug so it's independent of visibility /
 * site_source filters that drive the ordinary /family-trees list.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { TreePine, ArrowRight, BookOpen } from "lucide-react";

const FEATURED_SLUGS = ["vellaringattu-family", "kurisummoottil-family"];

export default function FeaturedTestTrees({ palette, title = "Featured family trees", subtitle }) {
  const P = palette || {
    saffron: "#B25D38", saffronSoft: "#F6DCC5",
    ink: "#2A1D10", inkSoft: "#5C4A32", gold: "#B98A2A",
  };
  const [trees, setTrees] = useState([]);

  useEffect(() => {
    let alive = true;
    Promise.allSettled(FEATURED_SLUGS.map((slug) => http.get(`/family-trees/${slug}`)))
      .then((res) => {
        if (!alive) return;
        setTrees(
          res
            .filter((r) => r.status === "fulfilled" && r.value?.data && !r.value.data.deleted)
            .map((r) => r.value.data)
        );
      });
    return () => { alive = false; };
  }, []);

  if (!trees.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="featured-test-trees">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4" style={{ color: P.gold }} />
          <h2 className="font-display text-2xl">{title}</h2>
        </div>
        {subtitle && <p className="text-xs mt-1" style={{ color: P.inkSoft }}>{subtitle}</p>}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {trees.map((t) => (
          <Link
            key={t.id || t.slug}
            to={`/family/${t.slug || t.id}`}
            className="rounded-xl border bg-white p-5 flex flex-col gap-2 hover:shadow-md transition"
            style={{ borderColor: P.saffronSoft }}
            data-testid={`featured-tree-${t.slug || t.id}`}
          >
            <div className="flex items-center gap-2">
              <TreePine className="w-5 h-5 shrink-0" style={{ color: P.saffron }} />
              <div className="font-display text-xl">{t.name}</div>
            </div>
            <div className="text-[11px] font-mono opacity-70">
              {t.origin_state ? `${t.origin_state} · ` : ""}
              {t.member_count ? `${t.member_count} documented members` : "new tree"}
              {t.site_source?.length ? ` · ${t.site_source.length} sites` : ""}
            </div>
            {t.summary && (
              <p className="text-sm mt-1 line-clamp-3" style={{ color: P.inkSoft }}>
                {t.summary}
              </p>
            )}
            <span
              className="text-sm font-semibold flex items-center gap-1 mt-1"
              style={{ color: P.saffron }}
            >
              Open the tree <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
